/* global SideDrawer, initLazyImages */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this.handleFiltersClick = this.handleFiltersClick.bind(this);
      this.handleFilterChange = this.handleFilterChange.bind(this);
      this.handleActiveFiltersClick = this.handleActiveFiltersClick.bind(this);
      this.breakpointChangeHandler = this.handleBreakpointChange.bind(this);
      this.historyChangeHandler = this.handleHistoryChange.bind(this);

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.normalizeCollectionUrl();

      window.addEventListener('popstate', this.historyChangeHandler);
      this.addEventListener('click', (evt) => {
        const clearAll = evt.target.closest('.js-clear-all');
        if (!clearAll) return;
        evt.preventDefault();
        this.applyFilters(new URL(clearAll.href, window.location.origin).searchParams.toString(), evt);
      });
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      this.bindFormListeners();

      if (this.filteringEnabled && !this.breakpointBound) {
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
        this.breakpointBound = true;
      }

      if (this.sortingEnabled && this.desktopSortBy && !this.desktopSortBound) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange);
        this.desktopSortBound = true;
      }
    }

    bindFormListeners() {
      if (!this.filteringEnabled) return;
      this.filters?.addEventListener('click', this.handleFiltersClick);
      this.filters?.addEventListener('input', this.handleFilterChange);
      this.filters?.addEventListener('change', this.handleFilterChange);
      this.activeFilters?.addEventListener('click', this.handleActiveFiltersClick);
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    isPriceControl(target) {
      return Boolean(target?.closest?.('price-range') || target?.dataset?.priceRange || target?.id?.includes('price-range'));
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const isPrice = this.isPriceControl(evt.target);
      const isSort = evt.target.id?.includes('sort-by') || evt.target.tagName === 'CUSTOM-SELECT';
      const isStatus = this.isStatusInput(evt.target);
      const isCategory = this.isCategoryInput(evt.target);

      if (evt.type === 'change' && !isPrice && !isSort) return;
      if (isPrice && evt.type === 'input') return;

      if ((isStatus || isCategory) && evt.target.checked) {
        this.form?.querySelectorAll(`input[name="${evt.target.name}"]`).forEach((input) => {
          if (input !== evt.target) input.checked = false;
        });
      }

      const timeoutDelay = isPrice || isStatus ? 0 : 150;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          const chosenSort = evt.detail?.selectedValue
            || (evt.target?.name === 'sort_by' ? evt.target.value : null)
            || new URL(window.location.href).searchParams.get('sort_by')
            || searchParams.get('sort_by');

          if (chosenSort) {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === chosenSort;
            });
            searchParams.delete('sort_by');
            searchParams.set('sort_by', chosenSort);
          }
        }

        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        if (isStatus && this.selectedStatuses(searchParams).length === 0) {
          searchParams.set('cpc_status', 'all');
        } else {
          searchParams.delete('cpc_status');
        }

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      const statusChip = evt.target.closest?.('.js-cpc-status-chip');
      if (statusChip) {
        evt.preventDefault();
        const params = new URLSearchParams(new FormData(this.form));
        const remove = (statusChip.dataset.status || '').toLowerCase();
        const kept = params.getAll(this.statusParamName())
          .filter((value) => value.toLowerCase() !== remove);
        params.delete(this.statusParamName());
        kept.forEach((value) => params.append(this.statusParamName(), value));
        if (kept.length === 0) params.set('cpc_status', 'all');
        this.applyFilters(params.toString(), evt);
        return;
      }

      const categoryChip = evt.target.closest?.('.js-cpc-category-chip');
      if (categoryChip) {
        evt.preventDefault();
        const params = new URLSearchParams(new FormData(this.form));
        params.delete('cpc_category');
        this.applyFilters(params.toString(), evt);
        return;
      }

      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    mergeSearchParams(formParams) {
      const next = new URLSearchParams(formParams);
      const current = new URL(window.location.href);

      current.searchParams.forEach((value, key) => {
        const isFilterParam = key.startsWith('filter.')
          || key === 'sort_by'
          || key === 'page'
          || key === 'section_id'
          || key === 'cpc_status'
          || key === 'cpc_availability'
          || key === 'cpc_from'
          || key === 'cpc_category'
          || key === 'view';
        if (!isFilterParam && !next.has(key)) {
          next.append(key, value);
        }
      });

      next.delete('page');
      next.delete('section_id');
      this.stripInvalidFilters(next);
      if (this.sortingEnabled && !next.get('sort_by')) {
        const currentSort = new URL(window.location.href).searchParams.get('sort_by');
        next.set('sort_by', currentSort || this.defaultSortValue());
      }
      this.ensureDefaultStatus(next);
      this.canonicalizeStatusParams(next);
      return next;
    }

    statusParamName() {
      return 'filter.p.m.custom.retail_availability';
    }

    isStatusInput(target) {
      const name = (target?.name || '').toLowerCase();
      return name.includes('retail_availability') || name === 'cpc_availability';
    }

    isCategoryInput(target) {
      return target?.name === 'cpc_category';
    }

    alwaysAllowedFilterPrefixes() {
      return [
        'filter.p.product_type',
        'filter.v.price',
        'filter.v.availability',
        'filter.p.m.custom.retail_availability',
        'filter.p.m.custom.availability_status'
      ];
    }

    knownFilterPrefixes() {
      return (this.dataset.knownFilters || '')
        .split('|')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    }

    hasLiveStatusFilter() {
      return false;
    }

    canonicalizeStatusParams(params) {
      const statuses = this.selectedStatuses(params);
      params.delete(this.statusParamName());
      params.delete('filter.p.m.custom.availability_status');
      params.delete('cpc_availability');
      if (params.get('cpc_status') === 'all') return params;
      statuses.forEach((value) => params.append('cpc_availability', value));
      return params;
    }

    selectedStatuses(params) {
      const retail = params.getAll(this.statusParamName());
      if (retail.length) return retail;
      return params.getAll('cpc_availability');
    }

    defaultStatusForCollection() {
      const handle = this.currentCollectionHandle();
      if (handle === (this.dataset.comingSoonHandle || 'coming-soon')) return 'Coming Soon';
      if (handle === (this.dataset.outOfStockHandle || 'cpc-out-of-stock')) return 'Out Of Stock';
      return 'In Stock';
    }

    ensureDefaultStatus(params) {
      if (this.currentCollectionHandle() === 'rare-plant-week') return params;
      if (params.get('cpc_status') === 'all') {
        params.delete(this.statusParamName());
        params.delete('cpc_availability');
        return params;
      }
      if (this.isSuppliesCollection(this.currentCollectionHandle())) {
        const allowed = this.selectedStatuses(params).filter((value) => {
          const status = this.normalizedStatus(value);
          return status === 'in stock' || status === 'out of stock';
        });
        params.delete(this.statusParamName());
        params.delete('cpc_availability');
        if (allowed.length) {
          allowed.forEach((value) => params.append(this.statusParamName(), value));
          return params;
        }
        params.set(this.statusParamName(), 'In Stock');
        return params;
      }
      if (this.selectedStatuses(params).length) return params;
      params.set(this.statusParamName(), this.defaultStatusForCollection());
      return params;
    }

    normalizedStatus(value) {
      return (value || '').trim().toLowerCase();
    }

    statusCollectionHandles() {
      return {
        comingSoon: this.dataset.comingSoonHandle || 'coming-soon',
        inStock: this.dataset.inStockHandle || 'cpc-in-stock',
        outOfStock: this.dataset.outOfStockHandle || 'cpc-out-of-stock'
      };
    }

    isStatusCollection(handle) {
      const current = (handle || '').toLowerCase();
      const { comingSoon, inStock, outOfStock } = this.statusCollectionHandles();
      return [comingSoon, inStock, outOfStock].includes(current);
    }

    statusCollectionHandle(params) {
      if (this.hasLiveStatusFilter()) return null;
      if (this.isSuppliesCollection(this.currentCollectionHandle())) return null;

      const current = this.currentCollectionHandle();
      const { comingSoon, outOfStock } = this.statusCollectionHandles();
      if (params.get('cpc_status') === 'all') {
        return this.isStatusCollection(current) ? (this.catalogHandles()[0] || 'houseplants') : null;
      }

      const status = this.normalizedStatus(this.selectedStatuses(params)[0]);
      if (status === 'coming soon') return comingSoon;
      if (status === 'out of stock') return outOfStock;
      if (this.categoryCollectionHandle(params)) return null;
      if (this.isStatusCollection(current)) return this.catalogHandles()[0] || 'houseplants';
      return null;
    }

    catalogHandles() {
      return (this.dataset.catalogHandles || 'houseplants,all-plants,all')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    }

    isCatalogCollection(handle) {
      return this.catalogHandles().includes((handle || '').toLowerCase());
    }

    suppliesHandles() {
      return (this.dataset.suppliesHandles || 'supplies-and-merch,merch,potting-soils')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    }

    isSuppliesCollection(handle) {
      return this.suppliesHandles().includes((handle || '').toLowerCase());
    }

    plantHandleMap() {
      return {
        'best sellers': this.dataset.categoryBestSellers || 'best-sellers',
        'pet friendly': this.dataset.categoryPetFriendly || 'pet-friendly',
        'easy care': this.dataset.categoryEasyCare || 'easy-low-maintenance-plants',
        'rare plants': this.dataset.categoryRarePlants || 'rare-and-uncommon-plants',
        'misc. plants': this.dataset.categoryMiscPlants || 'misc-plants'
      };
    }

    suppliesHandleMap() {
      return {
        soil: this.dataset.categorySoil || 'potting-soils',
        supplies: this.dataset.categorySupplies || 'supplies-and-merch',
        merch: this.dataset.categoryMerch || 'merch'
      };
    }

    categoryHandleMap() {
      if (this.isSuppliesCollection(this.currentCollectionHandle())) {
        return this.suppliesHandleMap();
      }
      return this.plantHandleMap();
    }

    categoryFromHandle(handle) {
      const current = (handle || '').toLowerCase();
      const parentSupplies = (this.dataset.categorySupplies || 'supplies-and-merch').toLowerCase();
      if (current === parentSupplies) return '';
      const map = this.categoryHandleMap();
      return Object.keys(map).find((label) => map[label] === current) || '';
    }

    impliedCategory() {
      const key = this.categoryFromHandle(this.currentCollectionHandle());
      if (key === 'best sellers') return 'Best Sellers';
      if (key === 'pet friendly') return 'Pet Friendly';
      if (key === 'easy care') return 'Easy Care';
      if (key === 'rare plants') return 'Rare Plants';
      if (key === 'misc. plants') return 'Misc. Plants';
      if (key === 'soil') return 'Soil';
      if (key === 'supplies') return 'Supplies';
      if (key === 'merch') return 'Merch';
      return '';
    }

    selectedCategories(params) {
      return params.getAll('cpc_category');
    }

    categoryCollectionHandle(params) {
      const selected = this.normalizedStatus(this.selectedCategories(params)[0]);
      return this.categoryHandleMap()[selected] || null;
    }

    filterFetchPath(params) {
      const status = this.normalizedStatus(this.selectedStatuses(params)[0]);
      const canStatusRoute = !this.isSuppliesCollection(this.currentCollectionHandle())
        && params.get('cpc_status') !== 'all';
      if (canStatusRoute && status === 'coming soon') {
        return `/collections/${this.dataset.comingSoonHandle || 'coming-soon'}`;
      }
      if (canStatusRoute && status === 'out of stock') {
        return `/collections/${this.dataset.outOfStockHandle || 'cpc-out-of-stock'}`;
      }

      const categoryHandle = this.categoryCollectionHandle(params);
      if (categoryHandle) return `/collections/${categoryHandle}`;

      const onCategoryPage = Boolean(this.categoryFromHandle(this.currentCollectionHandle()));
      const categoryCleared = onCategoryPage && this.selectedCategories(params).length === 0;
      if (categoryCleared) {
        if (this.isSuppliesCollection(this.currentCollectionHandle())) {
          return `/collections/${this.dataset.categorySupplies || 'supplies-and-merch'}`;
        }
        if (status === 'coming soon') return `/collections/${this.dataset.comingSoonHandle || 'coming-soon'}`;
        if (status === 'out of stock') return `/collections/${this.dataset.outOfStockHandle || 'cpc-out-of-stock'}`;
        return `/collections/${this.catalogHandles()[0] || 'houseplants'}`;
      }

      const handle = this.statusCollectionHandle(params);
      return handle ? `/collections/${handle}` : window.location.pathname;
    }

    currentCollectionHandle() {
      const parts = window.location.pathname.split('/collections/');
      return parts[1] ? parts[1].split('/')[0] : '';
    }

    paramsForFetch(params) {
      const next = new URLSearchParams(params);
      if (this.hasLiveStatusFilter()) return next;

      const statuses = this.selectedStatuses(next);
      next.delete(this.statusParamName());
      next.delete('cpc_availability');
      statuses.forEach((value) => next.append('cpc_availability', value));
      const fromHandle = this.currentCollectionHandle();
      const fetchHandle = (this.filterFetchPath(params).split('/collections/')[1] || '').split(/[/?#]/)[0];
      if (fromHandle && fetchHandle && fromHandle !== fetchHandle) {
        next.set('cpc_from', fromHandle);
      } else {
        next.delete('cpc_from');
      }

      next.delete('view');
      const fetchIsSupplies = this.isSuppliesCollection(fetchHandle);
      const statusHandle = this.statusCollectionHandle(params);
      const status = this.normalizedStatus(this.selectedStatuses(params)[0]);
      next.delete('filter.v.availability');
      if (status === 'in stock') next.set('filter.v.availability', '1');
      if (status === 'out of stock') next.set('filter.v.availability', '0');
      if (!statusHandle && !fetchIsSupplies) {
        if (params.get('cpc_status') === 'all') {
          next.set('view', 'cpc-all');
        } else if (status === 'in stock') {
          next.set('view', 'cpc-in-stock');
        }
      }
      return next;
    }

    stripNativeAvailability() {}

    isAllowedFilterKey(key) {
      const lower = key.toLowerCase();
      const allowed = [...this.alwaysAllowedFilterPrefixes(), ...this.knownFilterPrefixes()];
      return allowed.some((prefix) => lower === prefix || lower.startsWith(`${prefix}.`) || lower.startsWith(prefix));
    }

    stripInvalidFilters(params) {
      this.stripNativeAvailability(params);
      [...params.keys()].forEach((key) => {
        if (!key.startsWith('filter.')) return;
        if (!this.isAllowedFilterKey(key)) params.delete(key);
      });
    }

    syncStatusInputs(params) {
      const statuses = this.selectedStatuses(params);
      const selected = new Set(statuses.map((value) => value.toLowerCase()));
      this.form?.querySelectorAll(`input[name="${this.statusParamName()}"]`).forEach((input) => {
        input.checked = selected.has(input.value.toLowerCase());
      });

      const container = this.querySelector('.facets__active-filters');
      const list = this.querySelector('.active-filters');
      if (!container || !list) return;

      list.querySelectorAll('.js-cpc-status-chip').forEach((chip) => chip.remove());
      statuses.forEach((status) => {
        const chip = document.createElement('a');
        chip.href = '#';
        chip.className = 'active-filter flex items-center text-current js-cpc-status-chip';
        chip.dataset.status = status;
        chip.textContent = status;
        list.appendChild(chip);
      });

      list.querySelectorAll('.js-cpc-category-chip').forEach((chip) => chip.remove());
      const categories = this.selectedCategories(params);
      const selectedCategories = new Set(categories.map((value) => value.toLowerCase()));
      this.form?.querySelectorAll('input[name="cpc_category"]').forEach((input) => {
        input.checked = selectedCategories.has(input.value.toLowerCase());
      });
      categories.forEach((category) => {
        const chip = document.createElement('a');
        chip.href = '#';
        chip.className = 'active-filter flex items-center text-current js-cpc-category-chip';
        chip.dataset.category = category;
        chip.textContent = category;
        list.appendChild(chip);
      });

      const nativeCount = list.querySelectorAll('.active-filter:not(.js-cpc-status-chip):not(.js-cpc-category-chip)').length;
      container.hidden = nativeCount === 0 && statuses.length === 0 && categories.length === 0;
    }

    normalizeCollectionUrl() {
      const current = new URL(window.location.href);
      const next = new URLSearchParams(current.search);
      this.stripInvalidFilters(next);
      if (this.sortingEnabled && !next.get('sort_by')) {
        next.set('sort_by', this.defaultSortValue());
      }
      this.ensureDefaultStatus(next);
      this.canonicalizeStatusParams(next);
      if (!next.get('cpc_category')) {
        const implied = this.impliedCategory();
        if (implied) next.set('cpc_category', implied);
      }
      const urlChanged = next.toString() !== current.searchParams.toString();
      const fetchPath = this.filterFetchPath(next);
      const needsReroute = Boolean(fetchPath) && fetchPath !== current.pathname;
      if (!urlChanged && !needsReroute) return;
      this.applyFilters(next.toString(), null, true);
    }

    showFilterError(message) {
      if (!this.results) return;
      let banner = this.results.querySelector('.cpc-filters__error');
      if (!banner) {
        banner = document.createElement('p');
        banner.className = 'cpc-filters__error';
        banner.setAttribute('role', 'alert');
        this.results.prepend(banner);
      }
      banner.hidden = false;
      banner.textContent = message;
    }

    clearFilterError() {
      this.results?.querySelector('.cpc-filters__error')?.remove();
    }

    syncDesktopSort(sortValue) {
      if (!sortValue) return;
      const select = document.querySelector('#products-sort-by');
      if (!select) return;
      const options = select.querySelectorAll('[data-value]');
      let label = '';
      options.forEach((option) => {
        const selected = option.dataset.value === sortValue;
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) label = option.textContent.trim();
      });
      const buttonLabel = select.querySelector('.text-start');
      if (buttonLabel && label) buttonLabel.textContent = label;
      const selected = [...options].find((option) => option.dataset.value === sortValue);
      const listbox = select.querySelector('[role="listbox"]');
      if (listbox && selected?.id) listbox.setAttribute('aria-activedescendant', selected.id);
    }

    defaultSortValue(handle) {
      const value = (handle || this.currentCollectionHandle() || '').toLowerCase();
      if (value === 'supplies-and-merch') return 'manual';
      return 'title-ascending';
    }

    async applyFilters(searchParams, evt, updateUrl = true) {
      const mergedParams = this.mergeSearchParams(searchParams);
      const fetchPath = this.filterFetchPath(mergedParams);
      const destHandle = (fetchPath.split('/collections/')[1] || '').split(/[/?#]/)[0];
      const currentHandle = this.currentCollectionHandle();
      if (destHandle.toLowerCase() === 'supplies-and-merch' && currentHandle.toLowerCase() !== 'supplies-and-merch') {
        mergedParams.set('sort_by', 'manual');
      }
      const merged = mergedParams.toString();
      const requestId = (this.filterRequestId || 0) + 1;
      this.filterRequestId = requestId;

      try {
        const activeElementId = document.activeElement.id;

        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        const fetchParams = this.paramsForFetch(mergedParams);
        if (fetchPath && fetchPath !== window.location.pathname) {
          const query = fetchParams.toString();
          window.location.assign(query ? `${fetchPath}?${query}` : fetchPath);
          return;
        }

        this.results.classList.add('is-loading');
        this.results.setAttribute('aria-busy', 'true');
        this.clearFilterError();

        const closeBtn = this.querySelector('.js-close-drawer-mob');
        if (closeBtn) {
          closeBtn.ariaDisabled = 'true';
          closeBtn.classList.add('is-loading');
        }
        let fetchUrl = `${fetchPath}?${fetchParams.toString()}`;
        const hasCpcParams = [...fetchParams.keys()].some((key) => key.startsWith('cpc_'));
        if (fetchPath === window.location.pathname && this.form.dataset.filterSectionId && !hasCpcParams) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (requestId !== this.filterRequestId) return;

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          if (requestId !== this.filterRequestId) return;

          const nextFacets = tmpl.content.getElementById('facets');
          const nextResults = tmpl.content.getElementById('filter-results');
          if (!nextFacets || !nextResults) {
            this.showFilterError(theme.strings?.filterError || 'Unable to update results. Please try again.');
            return;
          }

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          nextFacets.querySelectorAll('details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = nextFacets.innerHTML;

          const nextCloseBtn = tmpl.content.querySelector('.js-close-drawer-mob');
          if (closeBtn && nextCloseBtn) closeBtn.innerText = nextCloseBtn.innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = nextResults.innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered form nodes only. Static listeners stay bound.
          this.addElements();
          this.bindFormListeners();
          this.syncStatusInputs(mergedParams);
          this.syncDesktopSort(mergedParams.get('sort_by'));

          // Reinitialize lazyload images after filters applied.
          if (typeof initLazyImages === 'function') initLazyImages();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          if (updateUrl) {
            const nextPath = this.filterFetchPath(mergedParams);
            FacetFilters.updateURL(fetchParams.toString(), nextPath);
          }

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          if (closeBtn) {
            closeBtn.classList.remove('is-loading');
            closeBtn.removeAttribute('aria-disabled');
          }

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        } else {
          this.showFilterError(theme.strings?.filterError || 'Unable to update results. Please try again.');
        }
      } catch (error) {
        if (error?.name === 'AbortError' || error === 'Request changed') return;
        this.showFilterError(theme.strings?.filterError || 'Unable to update results. Please try again.');
      } finally {
        if (requestId === this.filterRequestId) {
          this.results.classList.remove('is-loading');
          this.results.removeAttribute('aria-busy');
        }
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams, pathname = window.location.pathname) {
      window.history.pushState(
        { searchParams },
        '',
        `${pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
